import { Box, Button, Divider, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { gateway } from "../gateway";
import { createPlanState, resetCreatePlanState } from "../redux/features/createPlanStateSlice";
import { EBenefitTypes, getPlanById } from "../utils";
import { LoaderComponent } from "./Loader";
import { PlanBenefits } from "./planBenefits";
import { PlanPriceDetails } from "./planPriceDetails";
import { ProductDetails } from "./productDetails";

export const EditPlan = ({ id, isFreePlan = false, setDialogState = () => { }, resetDetails = () => { } }) => {
  const [productId, setProductId] = useState();
  const [productBdId, setProductBdId] = useState();
  const [isFormValid, setIsFormValid] = useState(true);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [hasTrial, setHastTrial] = useState(false);
  const [pricePlan, setPricePlan] = useState();
  const [priceTrial, setPriceTrial] = useState();
  const [coverageBenefit, setCoverageBenefit] = useState();
  const [connectionBenefit, setConnectionBenefit] = useState();

  const dispatch = useDispatch();
  const planState = useSelector((state) => state.createPlanState);
  const promoCodesState = useSelector((state) => state.promoCodesState);
  const [promoCodesBillingCycles, setPromoCodesBillingCycles] = useState([]);

  const token = sessionStorage.getItem("token");

  let trialCycle = null;
  let regularCycle = null;

  const getPlanDetails = async () => {
    try {
      const planDetails = await getPlanById(id, isFreePlan);
      setProductId(planDetails.product.paypalId);
      setProductBdId(planDetails?.product?.id);
      if (!isFreePlan) {
        planDetails.billing_cycles.forEach((cycle) => {
          if (cycle.tenure_type === "REGULAR") {
            regularCycle = cycle;
          } else if (cycle.tenure_type === "TRIAL") {
            trialCycle = cycle;
          }
        });
      }

      const planObject = {
        planName: planDetails.product.name,
        description: planDetails.description,
        status: planDetails.status,
        price: regularCycle?.pricing_scheme?.fixed_price?.value,
        billingFrequency: !isFreePlan ? setFrequency(regularCycle?.frequency?.interval_unit) : "Month",
        sequence: regularCycle?.sequence,
        totalCycles: regularCycle?.total_cycles,
        priceTrial: trialCycle?.pricing_scheme?.fixed_price?.value,
        sequenceTrail: trialCycle?.sequence,
        currency: !isFreePlan ? planDetails?.payment_preferences?.setup_fee?.currency_code : "USD",
        coverageBenefit: planDetails.product.benefits.find((item) => item.type === EBenefitTypes.AREASCOVERAGES)?.id,
        connectionBenefit: planDetails.product.benefits.find((item) => item.type === EBenefitTypes.FINDACLEANER)?.id,
      };

      if (planDetails?.billing_cycles?.length > 1) {
        setHastTrial(true);
        dispatch(createPlanState({ trial: true }));
      } else {
        dispatch(createPlanState({ trial: false }));
      }
      dispatch(createPlanState(planObject));
      setPriceTrial(planObject.priceTrial);
      setPricePlan(planObject.price);
      setCoverageBenefit(planObject.coverageBenefit);
      setConnectionBenefit(planObject.connectionBenefit);
    } catch (error) {
      console.log(error);
    }
  };

  const setFrequency = (value) => {
    switch (value) {
      case "MONTH":
        return "Month";
      case "WEEK":
        return "Week";
      case "YEAR":
        return "Year";
      default:
        return value;
    }
  };

  const updatePlan = async () => {
    try {
      setOpen(true);
      const productUpdateObject = [
        {
          op: "replace",
          path: "/name",
          value: planState.planName,
        },
      ];
      let benefits = null;

      const updatedPlan = {
        description: planState.description,
        name: planState.planName,
        taxPercentage: "0",
        autoBillOutstanding: true,
        paymentFailureThreshold: 7,
      };

      if (planState.price !== pricePlan) {
        updatedPlan.pricingSchemes = [
          {
            billingCycleSequence: planState.sequence,
            pricingScheme: {
              fixedPrice: {
                value: planState.price,
                currencyCode: planState.currency,
              },
            },
          },
        ];
        updateRelatedPromoDiscounts();
      }

      if (planState.coverageBenefit !== coverageBenefit || planState.connectionBenefit !== connectionBenefit) {
        benefits = {
          productId: productBdId,
          benefitIds: [planState.coverageBenefit, planState.connectionBenefit],
        };
        updatedPlan.benefits = benefits;
      }

      await gateway.patch(`/paypal/products/${productId}`, productUpdateObject, {
        headers: {
          Authorization: `${token}`,
        },
      });

      if (!isFreePlan) {
        await gateway.patch(`/paypal/plans/${id}`, updatedPlan, {
          headers: {
            Authorization: `${token}`,
          },
        });
      }

      if (isFreePlan && benefits) {
        await gateway.patch(`/paypal/plans-free/${id}`, updatedPlan, {
          headers: {
            Authorization: `${token}`,
          },
        });
      }
      if (hasTrial && planState.priceTrial !== priceTrial) {
        updatedPlan.benefits = undefined;
        updatedPlan.pricingSchemes[0].billingCycleSequence = planState.sequenceTrail;
        updatedPlan.pricingSchemes[0].pricingScheme.fixedPrice.value = planState.priceTrial;

        await gateway.patch(`/paypal/plans/${id}`, updatedPlan, {
          headers: {
            Authorization: `${token}`,
          },
        });
      }

      setDialogState();
      resetDetails();
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(resetCreatePlanState());
      setOpen(false);
    }
  };

  const getRelatedPromoDiscounts = () => {
    const promos = [];
    const promoCodes = promoCodesState.planPromoDiscount.find((item) => item.paypalPlanId === id);
    if (promoCodes) {
      promos.push(...promoCodes?.codes);
      setPromoCodesBillingCycles(promos);
    }
  };

  const updateRelatedPromoDiscounts = () => {
    if (promoCodesBillingCycles.length > 0) {
      const planDiscountObject = {
        description: planState.description,
        name: planState.planName,
        taxPercentage: "0",
        autoBillOutstanding: true,
        paymentFailureThreshold: 7,
        pricingSchemes: [
          {
            billingCycleSequence: 2,
            pricingScheme: {
              fixedPrice: {
                value: planState.price,
                currencyCode: planState.currency,
              },
            },
          },
        ],
      };
      promoCodesBillingCycles.forEach(async (plan) => {
        await gateway.patch(`/paypal/plans/${plan.planId}`, planDiscountObject, {
          headers: {
            Authorization: `${token}`,
          },
        });
      });
    }
  };

  function formatCurrency(value) {
    let formattedValue = Number(value).toFixed(2);
    let [integer, decimal] = formattedValue.split(".");
    integer = integer.padStart(2, "0");
    return `${integer}.${decimal}`;
  }

  useEffect(() => {
    getPlanDetails();
    getRelatedPromoDiscounts();
  }, []);

  return (
    <Box sx={{}}>
      <Typography fontWeight="bold">Review plan settings</Typography>
      <ProductDetails setIsFormValid={setIsFormValid} isFree={isFreePlan}></ProductDetails>
      <Divider></Divider>
      {!isFreePlan ? (
        <>
          <PlanPriceDetails setIsFormValid={setIsFormValid} onEdit={true}></PlanPriceDetails>
          <Divider></Divider>
        </>
      ) : (
        <></>
      )}

      <PlanBenefits setIsFormValid={setIsFormValid}></PlanBenefits>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Button variant="contained" color="primary" onClick={updatePlan}>
          Update Plan
        </Button>
      </Box>
      <LoaderComponent open={open} type={"spinner"}></LoaderComponent>
    </Box>
  );
};
